import React, { useEffect, useState } from 'react'
import moment from 'moment';
import { Button } from '@mui/material';

import { AppConfig } from '../../../config/appConfig';

import { ILoad_DeletedLight } from '../../../types/deleted';

import { UtilityService } from '../../../services/utilityService';

import { Icons } from '../../icons';

export interface ILoadTableProps {
	data?: ILoad_DeletedLight[];
	customer?: string;
	vendor?: string;
	trailer?: string;
	hwiNumber?: string;
	status?: string;
	onRestoreLoadRequest?: (loadId: number, bol?: string) => void;
}

const DeletedLoadTable: React.FunctionComponent<ILoadTableProps> = ({ data, customer, vendor, trailer, hwiNumber, status, onRestoreLoadRequest }) => {
	const [dataView, setDataView] = useState<ILoad_DeletedLight[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.deletedLoadfListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);

	useEffect(() => {
		if (data) {
			let newData = data.filter((item) => {
				if (customer && customer != "All" && item.customerName != customer) {
					return false;
				}
				if (vendor && vendor != "All" && item.vendorName != vendor) {
					return false;
				}
				if (trailer && trailer != "" && item.trailerNumber.toLowerCase().indexOf(trailer.toLowerCase()) < 0) {
					return false;
				}
				if (hwiNumber && hwiNumber != "" && item.hwiShortNumber.toLowerCase().indexOf(hwiNumber.toLowerCase()) < 0) {
					return false;
				}
				if (status && status != "All" && item.status != status) {
					return false;
				}
				return true;
			}).slice();

			// Start sort as descending if the field name starts with "-"
			let _sortField = sortField;
			if (sortField.startsWith("-")) {
				setSortAscending(false);
				_sortField = sortField.substring(1);
				setSortField(_sortField);
			}

			if (sortField) {
				switch (sortField) {
					case "trailer": {
						newData.sort((a, b) => UtilityService.compareStrings(a.trailerNumber, b.trailerNumber));
						break;
					}
					case "customer": {
						newData.sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName));
						break;
					}
					case "vendor": {
						newData.sort((a, b) => UtilityService.compareStrings(a.vendorName, b.vendorName));
						break;
					}
					case "plantweight": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.plantWeight, b.plantWeight));
						break;
					}
					case "requestdate": {
						newData.sort((a, b) => UtilityService.compareStrings(a.scheduledPickupDate?.toString(), b.scheduledPickupDate?.toString()));
						break;
					}
					case "status": {
						newData.sort((a, b) => UtilityService.compareStrings(a.status, b.status));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.hwiShortNumber, b.hwiShortNumber));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
		}
	}, [data, customer, vendor, trailer, hwiNumber, status, sortField, sortAscending])

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	const handleRestore = (shipmentId: number, bol?: string) => {
		onRestoreLoadRequest?.(shipmentId, bol);
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="col-bol">
					<p onClick={() => handleHeaderClick("bol")}>
						BOL #
						{" "}
						{(sortField == "bol" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "bol" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-trailer">
					<p onClick={() => handleHeaderClick("trailer")}>
						Trailer #
						{" "}
						{(sortField == "trailer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "trailer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-customer">
					<p onClick={() => handleHeaderClick("customer")}>
						Customer
						{" "}
						{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-vendor">
					<p onClick={() => handleHeaderClick("vendor")}>
						Vendor
						{" "}
						{(sortField == "vendor" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "vendor" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-status">
					<p onClick={() => handleHeaderClick("status")}>
						Status
						{" "}
						{(sortField == "status" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "status" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-deleted-date">
					<p onClick={() => handleHeaderClick("deleteddate")}>
						Deleted Date
						{" "}
						{(sortField == "deleteddate" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "deleteddate" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-deleted-by">
					<p onClick={() => handleHeaderClick("deletedby")}>
						Deleted By
						{" "}
						{(sortField == "deletedby" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "deletedby" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-controls">{""}</div>
			</div>
			<div className="data">
				{dataView?.length > 0 ?
					(
						dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item, index) => (
							<div className={"row"} key={item.id}>
								<div className="col-bol"><p>{item.hwiShortNumber}</p></div>
								<div className="col-trailer"><p>{item.trailerNumber}</p></div>
								<div className="col-customer"><p>{item.customerName}</p></div>
								<div className="col-vendor"><p>{item.vendorName}</p></div>
								<div className="col-status"><p>{item.status}</p></div>
								<div className="col-deleted-date"><p>{item.deletedDate ? moment(item.deletedDate).format("MM/DD/yyyy") : ""}</p></div>
								<div className="col-deleted-by"><p>{item.deletedBy}</p></div>
								<div className="col-controls">
									<Button onClick={() => { handleRestore(item.id, item.hwiNumber) }} color="primary">
										<Icons.LegacyData fill="#272f50" height="16px"></Icons.LegacyData>
										Restore
									</Button>
								</div>
							</div>
						))
					) :
					(
						<div className="no-results">0 results</div>
					)
				}
			</div>
			{dataView?.length > 0 &&
				<div className="pager">
					<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
					<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
					<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length - 1} onClick={handlePageForward}>Next</Button></div>
				</div>
			}
		</div>
	)
}

export default DeletedLoadTable;